import { useState, useCallback } from 'react';
import { Tag } from '../types'; // Assume this is where your Tag type lives
import { apiFetch } from '../../../api/core';
import { useCRMContext } from '../CRMContext';

export const useTagsMerge = () => {
  const [targetTag, setTargetTag] = useState<Tag | null>(null);
  const [sourceTags, setSourceTags] = useState<Tag[]>([]);
  const [isMerging, setIsMerging] = useState(false);

  const { apiPath } = useCRMContext();

  // 1. Start the merge process by selecting the initial target
  const startMerge = useCallback((tag: Tag) => {
    setTargetTag(tag);
    setSourceTags([]); // Reset sources if starting a new merge
  }, []);

  // 2. Add a tag to the source list (to be merged INTO the target)
  const addSource = useCallback((tag: Tag) => {
    // Prevent adding if it's already the target
    if (targetTag?._id === tag._id) return;
    
    // Prevent adding duplicate sources
    setSourceTags(prev => {
      if (prev.some(s => s._id === tag._id)) return prev;
      return [...prev, tag];
    });
  }, [targetTag]);

  // 3. Remove a specific source from the list
  const removeSource = useCallback((tagId: number) => {
    setSourceTags(prev => prev.filter(s => s._id !== tagId));
  }, []);

  // 4. Swap a source to become the new target
  const makeTarget = useCallback((newTarget: Tag) => {
    setTargetTag(prevTarget => {
      if (!prevTarget) return newTarget;
      
      setSourceTags(prevSources => {
        const filteredSources = prevSources.filter(s => s._id !== newTarget._id);
        return [...filteredSources, prevTarget];
      });
      
      return newTarget;
    });
  }, []);

  // 5. Cancel and completely close the merge panel
  const cancelMerge = useCallback(() => {
    setTargetTag(null);
    setSourceTags([]);
    setIsMerging(false);
  }, []);

  // Derived state for the UI
  const isPanelVisible = targetTag !== null;
  const canSubmitMerge = targetTag !== null && sourceTags.length > 0;

  // 6. Action to execute the merge
  const executeMerge = useCallback(async () => {
    if (!canSubmitMerge) return;

    setIsMerging(true);
    try {
      await apiFetch(`${apiPath}/tag/merge`, "post", {
        target_tag_id: targetTag._id,
        source_tags_ids: sourceTags.map(s => s._id),
      });
      
      // Clear state on success
      cancelMerge();
    } finally {
      setIsMerging(false);
    }
  }, [targetTag, sourceTags, canSubmitMerge, cancelMerge, apiPath]);

  return {
    targetTag,
    sourceTags,
    isMerging,
    isPanelVisible,
    canSubmitMerge,
    startMerge,
    addSource,
    removeSource,
    makeTarget,
    cancelMerge,
    executeMerge,
  };
};

export type TagsMergeData = ReturnType<typeof useTagsMerge>;